<template>
  <div class="summary-dashboard">
    <!-- Left Column: General + Incorporaciones + Coberturas -->
    <div class="left-column">
      <!-- General Section -->
      <div class="general-section">
        <h2><b>General</b></h2>
        <div class="section-layout">
          <div class="general-cards">
            <!-- Card 1: Colaboradores registrados -->
            <div class="general-card employees-registered">
              <div class="icon-container employees-registered-icon">
                <img src="@/assets/user-blue.svg" alt="Employees" />
              </div>
              <div class="card-content">
                <span class="card-number">{{ employeesCount }}</span>
                <span class="card-text">Colaboradores registrados</span>
              </div>
              <div
                class="incognito-icon"
                title="Es el número total de colaboradores activos que tenemos registrados en nuestra base de datos."
              >
                <img src="@/assets/incognit-blue-icon.svg" alt="Info" />
              </div>
            </div>
            <!-- Card 2: Colaboradores con cobertura -->
            <div class="general-card employees-covered">
              <div class="icon-container employees-covered-icon">
                <img src="@/onboarding/assets/membership-green.svg" alt="Employees with coverage" />
              </div>
              <div class="card-content">
                <span class="card-number">{{ employeesWithCoverage }}</span>
                <span class="card-text">Colaboradores con cobertura</span>
              </div>
              <div
                class="incognito-icon"
                title="Representa el número de colaboradores que tienen una Cobertura emitida que está activa."
              >
                <img src="@/assets/incognit-blue-icon.svg" alt="Info" />
              </div>
            </div>
            <!-- Card 3: Colaboradores sin cobertura -->
            <div class="general-card employees-without-coverage">
              <div class="icon-container employees-without-coverage-icon">
                <img src="@/assets/warning-icon.svg" alt="Employees without coverage" />
              </div>
              <div class="card-content">
                <span class="card-number">{{ employeesWithoutCoverage }}</span>
                <span class="card-text">Colaboradores sin cobertura</span>
              </div>
              <div
                class="incognito-icon"
                title="Colaboradores registrados en nuestra base de datos, pero que no cuentan con Cobertura aún o esta ha sido suspendida."
              >
                <img src="@/assets/incognit-blue-icon.svg" alt="Info" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Incorporaciones Section -->
      <div class="incorporaciones-section">
        <h2>Incorporaciones</h2>
        <div class="section-layout">
          <div class="incorporaciones-content">
            <div class="incorporaciones-status-section">
              <div class="section-header">
                <div class="icon-wrapper">
                  <img src="@/assets/add-user-green.svg" alt="People" />
                </div>
                <h3 class="subsection-title">Estatus de las incorporaciones</h3>
              </div>
              <div class="status-list">
                <!-- Status Item: Completado -->
                <div class="status-item completed">
                  <div class="status-header">
                    <span class="status-name">{{ IncorporationStatus.APROBACION }}</span>
                    <div class="status-count-wrapper">
                      <span class="status-count">{{ aprobationCount }}</span>
                    </div>
                  </div>
                  <div class="status-bar-container">
                    <div
                      class="status-bar"
                      :style="{ width: getStatusPercentage(aprobationCount) + '%' }"
                    ></div>
                  </div>
                </div>
                <!-- Status Item: En proceso -->
                <div class="status-item in-progress">
                  <div class="status-header">
                    <span class="status-name">{{ IncorporationStatus.EN_PROCESO }}</span>
                    <div class="status-count-wrapper">
                      <span class="status-count">{{ inProgressCount }}</span>
                    </div>
                  </div>
                  <div class="status-bar-container">
                    <div
                      class="status-bar"
                      :style="{ width: getStatusPercentage(inProgressCount) + '%' }"
                    ></div>
                  </div>
                </div>
                <!-- Status Item: No iniciado -->
                <div class="status-item not-started">
                  <div class="status-header">
                    <span class="status-name">{{ IncorporationStatus.NO_INICIADO }}</span>
                    <div class="status-count-wrapper">
                      <span class="status-count">{{ notStartedCount }}</span>
                    </div>
                  </div>
                  <div class="status-bar-container">
                    <div
                      class="status-bar"
                      :style="{ width: getStatusPercentage(notStartedCount) + '%' }"
                    ></div>
                  </div>
                </div>
                <!-- Status Item: Rechazado -->
                <div class="status-item rejected">
                  <div class="status-header">
                    <span class="status-name">{{ IncorporationStatus.RECHAZADO }}</span>
                    <div class="status-count-wrapper">
                      <span class="status-count">{{ rejectedCount }}</span>
                    </div>
                  </div>
                  <div class="status-bar-container">
                    <div
                      class="status-bar"
                      :style="{ width: getStatusPercentage(rejectedCount) + '%' }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Progreso de Incorporación on the right side -->
            <div class="incorporaciones-progress-chart">
              <div class="progress-header">
                <h3 class="subsection-title">Progreso de Incorporación</h3>
                <div class="progress-circle-wrapper">
                  <div
                    class="progress-circle"
                    :style="{ '--progress': incorporationProgressPercentage + '%' }"
                  >
                    <div class="inner-circle"></div>
                    <span class="progress-percentage">{{ incorporationProgressPercentage }}%</span>
                  </div>
                </div>
              </div>
              <p class="progress-text">De tus colaboradores completaron su incorporación</p>
              <button class="view-more-btn" @click="emit('tab-change', 'incorporaciones')">
                Ver más información
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Coberturas Section -->
      <div class="memberships-section">
        <h2><b>Coberturas Mutuus</b></h2>
        <div class="section-layout">
          <div class="memberships-content">
            <div class="membership-type-section">
              <div class="section-header">
                <div class="icon-wrapper">
                  <img src="@/onboarding/assets/membership-blue.svg" alt="Membership" />
                </div>
                <h3 class="subsection-title">Tipo de Cobertura</h3>
              </div>
              <div class="membership-types">
                <!-- Membership UNO -->
                <div class="membership-type-card">
                  <div class="card-box green">
                    <div class="icon-wrapper">
                      <img src="@/onboarding/assets/uno.svg" alt="Membership UNO" />
                    </div>
                    <span class="card-number">{{ membershipUnoCount }}</span>
                  </div>
                  <span class="card-text">Cobertura UNO</span>
                </div>
                <!-- Membership DOS -->
                <div class="membership-type-card">
                  <div class="card-box blue">
                    <div class="icon-wrapper">
                      <img src="@/onboarding/assets/dos.svg" alt="Membership DOS" />
                    </div>
                    <span class="card-number">{{ membershipDosCount }}</span>
                  </div>
                  <span class="card-text">Cobertura DOS</span>
                </div>
              </div>
            </div>
            <div class="membership-status-section">
              <div class="section-header">
                <div class="icon-wrapper">
                  <img src="@/assets/traffic-light-yellow.svg" alt="Status" />
                </div>
                <h3 class="subsection-title">Estatus de las Coberturas</h3>
              </div>
              <div class="membership-statuses">
                <!-- Status Vigente -->
                <div class="membership-status-card">
                  <div class="card-box green">
                    <div class="icon-wrapper">
                      <img src="@/assets/check-icon.svg" alt="Active" />
                    </div>
                    <span class="card-number">{{ activeMemberships }}</span>
                  </div>
                  <span class="card-text">Vigente</span>
                </div>
                <!-- Status Suspendido -->
                <div class="membership-status-card">
                  <div class="card-box orange">
                    <div class="icon-wrapper">
                      <img src="@/assets/triangle-info-white.svg" alt="Suspended" />
                    </div>
                    <span class="card-number">{{ suspendedMemberships }}</span>
                  </div>
                  <span class="card-text">Suspendido</span>
                </div>
                <!-- Status Cancelada -->
                <div class="membership-status-card">
                  <div class="card-box gray">
                    <div class="icon-wrapper">
                      <img src="@/assets/cancelled-white.svg" alt="Cancelled" />
                    </div>
                    <span class="card-number">{{ cancelledMemberships }}</span>
                  </div>
                  <span class="card-text">Cancelada</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <UsageSection :group-id="groupId" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue'
import { EmployeeDTO, MembershipStatusTranslation, IncorporationStatus } from 'commons'
import { ProductName } from 'commons'
import UsageSection from '@/onboarding/components/UsageSection.vue'
import { ApiService } from '../../services/apiService'

interface Props {
  groupId: string
}

const apiService = inject<ApiService>('apiService')

if (!apiService) {
  throw new Error('apiService is not provided')
}

const props = defineProps<Props>()

const emit = defineEmits(['tab-change'])

const employees = ref<EmployeeDTO[]>([])
const incorporationEmployees = ref<EmployeeDTO[]>([])

onMounted(async () => {
  await fetchEmployees()
  await fetchIncorporationEmployees()
})

// Usage section moved to UsageSection.vue

const employeesCount = computed(() => {
  return incorporationEmployees.value.length + employees.value.length
})

const employeesWithCoverage = computed(() => {
  return employees.value.filter(
    (emp) => emp.membershipStatus === MembershipStatusTranslation.VIGENTE,
  ).length
})

const employeesWithoutCoverage = computed(() => {
  const employeesWithInvalidMembership = employees.value.filter(
    (emp) =>
      emp.membershipStatus === MembershipStatusTranslation.CANCELADA ||
      emp.membershipStatus === MembershipStatusTranslation.SUSPENDIDA ||
      emp.membershipStatus === MembershipStatusTranslation.NO_EMITIDA,
  ).length
  return incorporationEmployees.value.length + employeesWithInvalidMembership
})

const membershipUnoCount = computed(() => {
  return employees.value.filter((emp) => emp.productName === ProductName.MM1).length
})

const membershipDosCount = computed(() => {
  return employees.value.filter((emp) => emp.productName === ProductName.MM2).length
})

const activeMemberships = computed(() => {
  return employees.value.filter(
    (emp) => emp.membershipStatus === MembershipStatusTranslation.VIGENTE,
  ).length
})

const suspendedMemberships = computed(() => {
  return employees.value.filter(
    (emp) => emp.membershipStatus === MembershipStatusTranslation.SUSPENDIDA,
  ).length
})

const cancelledMemberships = computed(() => {
  return employees.value.filter(
    (emp) => emp.membershipStatus === MembershipStatusTranslation.CANCELADA,
  ).length
})

const aprobationCount = computed(() => {
  return incorporationEmployees.value.filter(
    (emp) => emp.incorporationStatus?.toUpperCase() === IncorporationStatus.APROBACION,
  ).length
})

const inProgressCount = computed(() => {
  return incorporationEmployees.value.filter(
    (emp) => emp.incorporationStatus?.toUpperCase() === IncorporationStatus.EN_PROCESO,
  ).length
})

const notStartedCount = computed(() => {
  return incorporationEmployees.value.filter(
    (emp) => emp.incorporationStatus?.toUpperCase() === IncorporationStatus.NO_INICIADO,
  ).length
})

const rejectedCount = computed(() => {
  return incorporationEmployees.value.filter(
    (emp) => emp.incorporationStatus?.toUpperCase() === IncorporationStatus.RECHAZADO,
  ).length
})

const incorporationProgressPercentage = computed(() => {
  if (!incorporationEmployees.value || incorporationEmployees.value.length === 0) return 0
  const aprobationAndRejected = aprobationCount.value + rejectedCount.value
  return Math.round((aprobationAndRejected / incorporationEmployees.value.length) * 100)
})

const getStatusPercentage = (count: number) => {
  if (!incorporationEmployees.value || incorporationEmployees.value.length === 0) return 0
  return (count / incorporationEmployees.value.length) * 100
}

const fetchEmployees = async () => {
  try {
    const employeesData = await apiService.getEmployeesByGroup(props.groupId, true)
    employees.value = employeesData
  } catch (err) {
    console.error('Hubo un error: Error fetching employees ', err)
  }
}

const fetchIncorporationEmployees = async () => {
  try {
    const incorporationData = await apiService.getIncorporationEmployeesByGroup(props.groupId)
    incorporationEmployees.value = incorporationData
  } catch (err) {
    console.error('Hubo un error: Error fetching incorporation employees ', err)
  }
}
</script>

<style scoped>
/* Frame 3466174 - .summary-dashboard */
.summary-dashboard {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 40px;
  gap: 40px;
  height: auto;
  min-height: auto;
  background: #f8f8f8;
  border-radius: 24px;
  box-sizing: border-box;
  margin: 0 auto;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  overflow-y: visible;
  position: relative;
}

/* Frame 3466170 - .left-column */
.left-column {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 60px;
  width: 100%;
  max-width: 100%;
  height: auto;
  min-height: auto;
  flex: 1 1 auto;
  order: 0;
  flex-grow: 1;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}

/* Frame 3466168 - .general-section */
.general-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  min-height: 174px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}

/* Frame 3466167 - .general-cards */
.general-cards {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 40px;
  width: 100%;
  max-width: 100%;
  min-height: 116px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}

/* Responsive layout for general cards on smaller screens */
@media (max-width: 1200px) {
  .general-cards {
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
  }

  .general-card {
    width: 100%;
    min-height: 116px;
  }

  .general-card .card-content {
    width: 100%;
    max-width: 100%;
  }

  .general-card .card-text {
    width: 100%;
    max-width: 100%;
  }
}

/* Additional responsive improvements for mobile devices */
@media (max-width: 768px) {
  .general-cards {
    gap: 16px;
  }

  .general-card {
    padding: 20px 16px;
    gap: 12px;
  }

  .general-card .card-content {
    width: auto;
    flex-grow: 1;
  }

  .general-card .card-text {
    width: auto;
    white-space: normal;
  }

  .incorporaciones-content {
    padding: 60px 24px 32px 24px;
  }
}

/* Frame 3466127, 3466122, 3466128 - .general-card */
.general-card {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 24px;
  gap: 16px;
  isolation: isolate;
  min-height: 116px;
  background: #ffffff;
  border: 1px solid #d5d5d5;
  border-radius: 16px;
  flex: 1 1 auto;
  order: 0;
  flex-grow: 1;
  position: relative;
  min-width: 0;
  box-sizing: border-box;
}

.general-card .icon-container {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  order: 0;
  flex-grow: 0;
  z-index: 0;
}

.employees-registered-icon {
  background: #b2e0ff;
}

.employees-covered-icon {
  background: #ccffc1;
}

.employees-without-coverage-icon {
  background: #efcecc;
}

/* Vector inside icon containers - .general-card .icon-container img */
.general-card .icon-container img {
  width: 30px;
  height: 30px;
  object-fit: contain;
}

/* Frame 3466165 - .general-card .card-content */
.general-card .card-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
  width: 157px;
  min-width: 0;
  max-width: 100%;
  height: auto;
  min-height: 68px;
  flex: 1 1 auto;
  order: 2;
  flex-grow: 1;
  z-index: 2;
  box-sizing: border-box;
}

/* 26, 7 (numbers) - .general-card .card-number */
.general-card .card-number {
  width: 45px;
  height: 47px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 700;
  font-size: 36px;
  line-height: 130%;
  display: flex;
  align-items: center;
  text-align: center;
  color: #2b2a2a;
  flex: none;
  order: 0;
  flex-grow: 0;
}

/* Colaboradores registrados (text) - .general-card .card-text */
.general-card .card-text {
  width: 100%;
  max-width: 100%;
  min-height: 17px;
  height: auto;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 130%;
  color: #2b2a2a;
  flex: none;
  order: 1;
  flex-grow: 0;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  box-sizing: border-box;
}

/* ALERTS (incognito icon) - .incognito-icon */
.incognito-icon {
  position: absolute;
  width: 32px;
  height: 32px;
  right: 7.67px;
  top: 8px;
  flex: none;
  order: 3;
  flex-grow: 0;
  z-index: 3;
}

/* Vector inside ALERTS - .incognito-icon img */
.incognito-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Frame 3466169 - .incorporaciones-section */
.incorporaciones-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  min-height: auto;
  height: auto;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}

/* Frame 3466134 - .incorporaciones-content */
.incorporaciones-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 80px 40px 40px 40px;
  gap: 24px;
  width: 100%;
  min-height: auto;
  height: auto;
  background: #ffffff;
  border: 1px solid #d5d5d5;
  border-radius: 24px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
  max-width: 100%;
  overflow-x: hidden;
  overflow-y: visible;
}

/* Responsive layout for incorporaciones content on smaller screens */
@media (max-width: 1200px) {
  .incorporaciones-content {
    flex-direction: column;
    align-items: stretch;
    gap: 32px;
    padding: 60px 32px 40px 32px;
  }

  .incorporaciones-status-section,
  .incorporaciones-progress-chart {
    width: 100%;
    max-width: 100%;
  }

  .incorporaciones-status-section .status-list {
    padding: 0px 32px 0px 32px;
  }
}

/* Additional responsive adjustments for smaller screens */
@media (max-width: 1400px) {
  .summary-dashboard {
    padding: 28px;
    gap: 28px;
  }

  .general-cards {
    gap: 24px;
  }

  .incorporaciones-content {
    padding: 60px 28px 40px 28px;
    gap: 18px;
  }

  .incorporaciones-status-section .status-list {
    padding: 0px 32px 0px 32px;
  }
}

/* Specific adjustments for 1280px width screens */
@media (max-width: 1320px) {
  .summary-dashboard {
    padding: 20px;
    gap: 20px;
  }

  .left-column {
    gap: 40px;
  }

  .general-cards {
    gap: 16px;
  }

  .general-card {
    padding: 16px;
    min-width: 0;
    flex: 1 1 0;
  }

  .general-card .card-content {
    width: 100%;
    max-width: 100%;
    flex: 1 1 auto;
  }

  .general-card .card-text {
    width: 100%;
    max-width: 100%;
    font-size: 12px;
  }

  .incorporaciones-content {
    padding: 40px 20px 24px 20px;
    gap: 12px;
  }

  .incorporaciones-status-section {
    width: 55%;
    max-width: 55%;
    flex: 1 1 55%;
    min-width: 0;
  }

  .incorporaciones-progress-chart {
    width: 40%;
    max-width: 40%;
    flex: 1 1 40%;
    min-width: 0;
  }

  .incorporaciones-status-section .status-list {
    padding: 0px 16px 0px 16px;
    gap: 20px;
  }

  .status-item {
    width: 100%;
    max-width: 100%;
    gap: 12px;
  }

  .status-item .status-header {
    width: 100%;
    max-width: 100%;
    gap: 8px;
  }

  .status-item .status-name {
    width: auto;
    max-width: 50%;
    flex: 0 1 auto;
    font-size: 16px;
  }

  .status-item .status-bar-container {
    width: 100%;
    max-width: 100%;
  }

  .incorporaciones-status-section .section-header .subsection-title {
    width: auto;
    max-width: 100%;
    font-size: 18px;
  }

  .incorporaciones-progress-chart .progress-text {
    width: 100%;
    max-width: 100%;
    padding: 0 8px;
    min-height: auto;
    height: auto;
  }

  .incorporaciones-progress-chart .view-more-btn {
    width: 100%;
    max-width: 100%;
    padding: 12px 8px;
    font-size: 14px;
    min-height: auto;
  }

  .incorporaciones-progress-chart .progress-header {
    width: 100%;
    max-width: 100%;
    padding: 0 8px;
  }

  .incorporaciones-progress-chart .progress-circle-wrapper {
    width: 180px;
    max-width: 100%;
    height: 180px;
  }

  .incorporaciones-progress-chart .progress-header .subsection-title {
    width: 100%;
    max-width: 100%;
    font-size: 16px;
    padding: 0 8px;
  }
}

/* Additional responsive improvements for mobile devices */
@media (max-width: 768px) {
  .incorporaciones-content {
    padding: 40px 24px 32px 24px;
    gap: 24px;
  }

  .incorporaciones-status-section {
    padding: 32px 16px;
    gap: 32px;
  }

  .incorporaciones-progress-chart {
    padding: 32px 16px;
    gap: 32px;
  }

  .incorporaciones-status-section .section-header {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }

  .incorporaciones-status-section .section-header .subsection-title {
    width: auto;
    height: auto;
  }

  .incorporaciones-status-section .status-list {
    padding: 0px 32px 0px 32px;
    gap: 24px;
  }
}

/* Frame 3466133 - .incorporaciones-status-section */
.incorporaciones-status-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px 0px;
  gap: 48px;
  margin: 0;
  width: 60%;
  min-height: auto;
  height: auto;
  background: #ffffff;
  border-radius: 16px;
  flex: 1 1 60%;
  order: 0;
  align-self: stretch;
  min-width: 0;
  max-width: 60%;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}

/* Frame 3466131 - .incorporaciones-status-section .section-header */
.incorporaciones-status-section .section-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  min-height: 40px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}

/* Frame 3466128 (icon container) - .incorporaciones-status-section .section-header .icon-wrapper */
.incorporaciones-status-section .section-header .icon-wrapper {
  width: 40px;
  height: 40px;
  background: #bdff9d;
  border-radius: 4px;
  flex: none;
  order: 0;
  flex-grow: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* PEOPLE/Vector - .incorporaciones-status-section .section-header .icon-wrapper img */
.incorporaciones-status-section .section-header .icon-wrapper img {
  width: 24px;
  height: 16px;
  object-fit: contain;
}

/* Frame 3466108 - .incorporaciones-status-section .section-header .subsection-title */
.incorporaciones-status-section .section-header .subsection-title {
  width: 363px;
  max-width: 100%;
  height: 34px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 400;
  font-size: 24px;
  line-height: 140%;
  text-align: center;
  color: #2b2a2a;
  flex: none;
  order: 1;
  flex-grow: 0;
  box-sizing: border-box;
}

/* Frame 233 - .incorporaciones-status-section .status-list */
.incorporaciones-status-section .status-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px 40px 0px 40px;
  gap: 32px;
  width: 100%;
  min-height: auto;
  height: auto;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
  max-width: 100%;
  overflow-x: hidden;
  overflow-y: visible;
}

/* Frame 228, 229, 230, 231 - .status-item */
.status-item {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  height: 73px;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
  margin-left: 0px;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}

/* Frame 3466188 - .status-item .status-header */
.status-item .status-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  height: 41px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}

/* Completado (text) - .status-item .status-name */
.status-item .status-name {
  width: 150px;
  min-width: 0;
  max-width: 100%;
  min-height: 25px;
  height: auto;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 140%;
  display: flex;
  align-items: center;
  color: #2b2a2a;
  flex: 0 1 auto;
  order: 0;
  flex-grow: 0;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  box-sizing: border-box;
}

/* Frame 3466187 - .status-item .status-count-wrapper */
.status-item .status-count-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 8px;
  margin-left: auto;
  margin-right: 0;
  width: 44px;
  height: 41px;
  background: #eaeaea;
  border-radius: 8px;
  flex: none;
  order: 1;
  flex-grow: 0;
}

/* 9 (number) - .status-item .status-count */
.status-item .status-count {
  width: 12px;
  height: 25px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 100%;
  display: flex;
  align-items: center;
  text-align: right;
  color: #2b2a2a;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
}

/* Frame 234 - .status-item .status-bar-container */
.status-item .status-bar-container {
  width: 100%;
  max-width: 100%;
  height: 16px;
  background: #eaeaea;
  border-radius: 32px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
}

/* Rectangle 19, 20, 21, 22 - .status-item .status-bar */
.status-item .status-bar {
  position: absolute;
  left: 0px;
  top: 0%;
  bottom: 0%;
  height: 100%;
  border-radius: 0px 24px 24px 0px;
  transition: width 0.3s ease;
}

.status-item.completed .status-bar {
  background: #9cd41c;
}

.status-item.in-progress .status-bar {
  background: #0059ef;
}

.status-item.not-started .status-bar {
  background: #f9cf45;
}

.status-item.rejected .status-bar {
  background: #d0342c;
}

/* Frame 3466173 - .incorporaciones-progress-chart */
.incorporaciones-progress-chart {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 24px 0px;
  gap: 40px;
  margin: 0;
  width: 35%;
  min-height: auto;
  height: auto;
  background: #ffffff;
  border-radius: 16px;
  flex: 1 1 35%;
  order: 1;
  align-self: stretch;
  min-width: 0;
  max-width: 35%;
  box-sizing: border-box;
}

/* Frame 3466177 - .incorporaciones-progress-chart .progress-header */
.incorporaciones-progress-chart .progress-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 24px;
  width: 289px;
  max-width: 100%;
  min-height: 355px;
  height: auto;
  flex: none;
  order: 0;
  flex-grow: 0;
  box-sizing: border-box;
}

/* Progreso de Incorporación - .incorporaciones-progress-chart .progress-header .subsection-title */
.incorporaciones-progress-chart .progress-header .subsection-title {
  width: 251px;
  max-width: 100%;
  min-height: 25px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 140%;
  color: #000000;
  flex: none;
  order: 0;
  flex-grow: 0;
  box-sizing: border-box;
  word-wrap: break-word;
  text-align: center;
}

/* Frame 3466178 - .incorporaciones-progress-chart .progress-circle-wrapper */
.incorporaciones-progress-chart .progress-circle-wrapper {
  width: 236px;
  max-width: 100%;
  height: 236px;
  flex: none;
  order: 1;
  flex-grow: 0;
  position: relative;
  box-sizing: border-box;
}

/* Ellipse 5 - .progress-circle (outer ring) */
.progress-circle {
  position: absolute;
  left: -0.32%;
  right: -0.32%;
  top: 0%;
  bottom: 0%;
  background: #f2f2f2;
  border-radius: 50%;
  background: conic-gradient(
    var(--progress-color, #9cd41c) var(--progress),
    #f2f2f2 var(--progress)
  );
}

/* Ellipse 4 - .incorporaciones-progress-chart .inner-circle */
.incorporaciones-progress-chart .inner-circle {
  position: absolute;
  left: 12.81%;
  right: 12.89%;
  top: 13.09%;
  bottom: 13.09%;
  background: #ffffff;
  box-shadow: 0px 0px 8px 1px rgba(0, 0, 0, 0.25);
  border-radius: 50%;
}

/* 40% - .incorporaciones-progress-chart .progress-percentage */
.incorporaciones-progress-chart .progress-percentage {
  position: absolute;
  width: 70px;
  height: 38px;
  left: calc(50% - 70px / 2);
  top: calc(50% - 38px / 2 - 0.91px);
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 600;
  font-size: 32px;
  line-height: 120%;
  display: flex;
  align-items: center;
  text-align: center;
  letter-spacing: -0.02em;
  color: var(--primary-blue);
}

/* De tus colaboradores completaron su incorporación - .incorporaciones-progress-chart .progress-text */
.incorporaciones-progress-chart .progress-text {
  width: 289px;
  max-width: 100%;
  min-height: 46px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 150%;
  text-align: center;
  color: #000000;
  flex: none;
  order: 1;
  flex-grow: 0;
  margin-top: -75px;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* BUTTONS - .incorporaciones-progress-chart .view-more-btn */
.incorporaciones-progress-chart .view-more-btn {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 16px 12px;
  gap: 4px;
  width: 240px;
  max-width: 100%;
  min-height: 56px;
  height: auto;
  background: var(--primary-blue);
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  border-radius: 16px;
  flex: none;
  order: 2;
  flex-grow: 0;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 140%;
  text-align: center;
  color: #ffffff;
  border: none;
  cursor: pointer;
  box-sizing: border-box;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Frame 3466170 (inner) - .memberships-section */
.memberships-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 24px;
  width: 100%;
  height: 280px;
  flex: none;
  order: 2;
  align-self: stretch;
  flex-grow: 0;
}

/* Frame 3466134 (inner) - .memberships-content */
.memberships-content {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 40px;
  width: 100%;
  min-height: 200px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
}

/* Responsive layout for smaller screens */
@media (max-width: 1200px) {
  .memberships-content {
    flex-direction: column;
    align-items: stretch;
    gap: 24px;
  }

  .membership-type-section,
  .membership-status-section {
    width: 100%;
    min-height: 200px;
  }
}

/* Additional responsive improvements for smaller screens */
@media (max-width: 768px) {
  .membership-type-section,
  .membership-status-section {
    padding: 24px 16px;
    gap: 32px;
  }

  .membership-type-section .section-header,
  .membership-status-section .section-header {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }

  .membership-type-section .section-header .subsection-title,
  .membership-status-section .section-header .subsection-title {
    width: auto;
    height: auto;
  }

  .membership-types,
  .membership-statuses {
    gap: 40px;
  }
}

/* Frame 3466132 - .membership-type-section */
.membership-type-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 32px 32px;
  gap: 48px;
  /* width: 626px; */
  min-height: 200px;
  background: #ffffff;
  border: 1px solid #d5d5d5;
  border-radius: 16px;
  flex: none;
  order: 0;
  flex-grow: 1;
}

/* Frame 3466131 - .membership-type-section .section-header */
.membership-type-section .section-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 16px;
  width: 100%;
  min-height: 40px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
}

/* Frame 3466128 (icon container) - .membership-type-section .section-header .icon-wrapper */
.membership-type-section .section-header .icon-wrapper {
  width: 40px;
  height: 40px;
  background: #bedfff;
  border-radius: 4px;
  flex: none;
  order: 0;
  flex-grow: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* VARIOUS/Vector - .membership-type-section .section-header .icon-wrapper img */
.membership-type-section .section-header .icon-wrapper img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

/* Frame 3466108 - .membership-type-section .section-header .subsection-title */
.membership-type-section .section-header .subsection-title {
  width: 231px;
  height: 34px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 400;
  font-size: 24px;
  line-height: 140%;
  text-align: center;
  color: #2b2a2a;
  flex: none;
  order: 1;
  flex-grow: 0;
}

/* Frame 3466126 - .membership-type-section .membership-types */
.membership-type-section .membership-types {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 80px;
  width: 308px;
  height: 88px;
  flex: none;
  order: 1;
  flex-grow: 0;
}

/* UNO, DOS - .membership-type-card */
.membership-type-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 8px;
  width: 114px;
  height: 88px;
  flex: none;
  flex-grow: 0;
}

/* Frame 3466122 - .membership-type-card .card-box */
.membership-type-card .card-box {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px;
  gap: 16px;
  isolation: isolate;
  width: 114px;
  height: 63px;
  border-radius: 8px;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
}

.membership-type-card .card-box.green {
  background: #6dbc24;
}

.membership-type-card .card-box.blue {
  background: var(--primary-blue);
}

/* VARIOUS (icon container) - .membership-type-card .card-box .icon-wrapper */
.membership-type-card .card-box .icon-wrapper {
  width: 32px;
  height: 32px;
  flex: none;
  order: 0;
  flex-grow: 0;
  z-index: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Vector/UNO/Union - .membership-type-card .card-box .icon-wrapper img */
.membership-type-card .card-box .icon-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 6, 1 (numbers) - .membership-type-card .card-number */
.membership-type-card .card-number {
  width: 50px;
  height: 47px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 36px;
  line-height: 130%;
  display: flex;
  align-items: center;
  text-align: center;
  color: #ffffff;
  flex: none;
  order: 1;
  flex-grow: 0;
  z-index: 1;
}

/* Membresía UNO, Membresía DOS (text) - .membership-type-card .card-text */
.membership-type-card .card-text {
  width: 109px;
  height: 17px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 130%;
  text-align: center;
  color: #2b2a2a;
  flex: none;
  order: 1;
  flex-grow: 0;
}

/* Frame 3466133 (second one) - .membership-status-section */
.membership-status-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 32px 32px;
  gap: 48px;
  /* width: 626px; */
  min-height: 200px;
  background: #ffffff;
  border: 1px solid #d5d5d5;
  border-radius: 16px;
  flex: none;
  order: 1;
  flex-grow: 1;
}

/* Frame 3466131 - .membership-status-section .section-header */
.membership-status-section .section-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 16px;
  width: 100%;
  min-height: 40px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
}

/* Frame 3466128 (icon container) - .membership-status-section .section-header .icon-wrapper */
.membership-status-section .section-header .icon-wrapper {
  width: 40px;
  height: 40px;
  background: #faf1c9;
  border-radius: 4px;
  flex: none;
  order: 0;
  flex-grow: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* VARIOUS/Vector - .membership-status-section .section-header .icon-wrapper img */
.membership-status-section .section-header .icon-wrapper img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

/* Frame 3466108 - .membership-status-section .section-header .subsection-title */
.membership-status-section .section-header .subsection-title {
  width: 318px;
  height: 34px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 400;
  font-size: 24px;
  line-height: 140%;
  text-align: center;
  color: #2b2a2a;
  flex: none;
  order: 1;
  flex-grow: 0;
}

/* Frame 3466126 - .membership-status-section .membership-statuses */
.membership-status-section .membership-statuses {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 80px;
  width: 100%;
  min-height: 88px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
}

/* Frame 3466123, 3466125, 3466127 - .membership-status-card */
.membership-status-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 8px;
  width: 114px;
  height: 88px;
  flex: none;
  flex-grow: 0;
}

/* Frame 3466122 - .membership-status-card .card-box */
.membership-status-card .card-box {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px;
  gap: 16px;
  width: 114px;
  height: 63px;
  border-radius: 8px;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
}

.membership-status-card .card-box.green {
  background: #4eab1f;
}

.membership-status-card .card-box.orange {
  background: #da8d11;
}

.membership-status-card .card-box.gray {
  background: #6b6b6b;
}

/* ALERTS (icon container) - .membership-status-card .card-box .icon-wrapper */
.membership-status-card .card-box .icon-wrapper {
  width: 32px;
  height: 32px;
  flex: none;
  order: 0;
  flex-grow: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Vector - .membership-status-card .card-box .icon-wrapper img */
.membership-status-card .card-box .icon-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 7, 0, 1 (numbers) - .membership-status-card .card-number */
.membership-status-card .card-number {
  width: 50px;
  height: 47px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 36px;
  line-height: 130%;
  display: flex;
  align-items: center;
  text-align: center;
  color: #ffffff;
  flex: none;
  order: 1;
  flex-grow: 0;
}

/* Vigente, Suspendido, Cancelada (text) - .membership-status-card .card-text */
.membership-status-card .card-text {
  width: 51px;
  height: 17px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 130%;
  text-align: center;
  color: #2b2a2a;
  flex: none;
  order: 1;
  flex-grow: 0;
}

/* Section Layout - positions title to the left of content */
.section-layout {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;
  width: 100%;
}
</style>
